import config from '/config.js';
import { bbStart, bbStop } from '/bb/index.js';
import { serializeMetrics } from '/bb/serializer.js';
import { getState } from '/bb/state.js';
import { getBbDebugState } from '../bb/debug.js';
import { getBbIntegrationState } from '../bb/integration-state.js';

const SAVE_HOOK_NAME = '__RS_BB_DEVTOOLS_SAVE_HOOK__';
const API_KEY_GLOBAL = '__RS_API_KEY__';
const BB_DEBUG_SESSION_PATH = '/bb/debug/sessions/';
const PROCESSED_FETCH_INITIAL_DELAY_MS = 3000;
const PROCESSED_FETCH_RETRIES = 8;
const PROCESSED_FETCH_RETRY_MS = 250;

export function createBbDevToolsBridge(rsSDK) {
  const saves = [];
  let nextSaveId = 1;

  const bridge = {
    install,
    read,
    openSnapshot,
    getSaves,
    resetSession,
    beforeSave,
    afterSave,
    holdTimeout,
  };

  return bridge;

  function install() {
    if (typeof window === 'undefined') return;

    window[SAVE_HOOK_NAME] = {
      beforeSave,
      afterSave,
      holdTimeout,
    };
  }

  function read() {
    return {
      debug: readDebugState(),
      config: readConfigState(),
      metrics: readMetricsSnapshot(),
      saves: getSaves(),
      latest_processed: readLatestProcessed(),
    };
  }

  function openSnapshot() {
    return readMetricsSnapshot();
  }

  function getSaves() {
    return cloneJson(saves);
  }

  function resetSession() {
    const integration = getBbIntegrationState();
    const shouldRestart = !!(integration.appId && integration.enabled);

    return Promise.resolve(bbStop()).then(function () {
      saves.length = 0;
      nextSaveId = 1;

      if (shouldRestart) {
        return Promise.resolve(bbStart()).then(function () {
          return read();
        });
      }

      return read();
    });
  }

  function beforeSave(payload) {
    const startedAt = payload && payload.startedAt ? payload.startedAt : Date.now();
    const entry = {
      id: nextSaveId++,
      status: 'saving',
      save_status: 'saving',
      started_at: new Date(startedAt).toISOString(),
      duration_ms: null,
      session_id: payload && payload.sessionId ? payload.sessionId : null,
      app_id: payload && payload.appId ? payload.appId : null,
      error: null,
      hold_timeout: false,
      sent_payload: {
        metrics: cloneJson(payload && payload.metrics ? payload.metrics : {}),
      },
      processed_status: 'idle',
      processed: null,
      processed_error: null,
    };

    saves.unshift(entry);
    if (saves.length > 20) saves.pop();
  }

  function afterSave(payload) {
    const result = payload && payload.result ? payload.result : {};
    const sessionId = result.session_id || (payload && payload.sessionId) || null;
    const entry = findSaveEntry(sessionId) || createLateSaveEntry(payload, result);

    entry.duration_ms = payload && Number.isFinite(payload.durationMs) ? payload.durationMs : null;
    entry.session_id = sessionId;
    entry.save_status = result.error ? 'error' : 'ok';
    entry.status = entry.hold_timeout ? 'timeout' : entry.save_status;
    entry.error = result.error || null;

    if (!result.error && sessionId) {
      fetchProcessedAfterDelay(entry, sessionId);
    }
  }

  function holdTimeout() {
    const entry = findFirstSavingEntry();

    if (!entry) return;

    entry.hold_timeout = true;
    entry.status = 'timeout';
  }

  function readDebugState() {
    try {
      const integration = getBbIntegrationState();
      return getBbDebugState({ getAppId: () => integration.appId || '' }) || {};
    } catch (e) {
      return { error: e && e.message ? e.message : String(e) };
    }
  }

  function readMetricsSnapshot() {
    try {
      return serializeMetrics(getState(), Date.now());
    } catch (e) {
      return {
        error: e && e.message ? e.message : String(e),
      };
    }
  }

  function readConfigState() {
    const debug = readDebugState();
    const integration = getBbIntegrationState();

    return {
      app_id: debug && debug.configured ? '(configured)' : '',
      api_host: integration.apiHost || readDefaultEndpoint(),
      enabled: integration.enabled !== false,
      auto: !!integration.auto,
      mode: integration.mode,
      attributes_mode: 'merge',
      timeout: '',
      hold_submit_timeout: String(integration.holdSubmitTimeoutMs),
      debug_enabled: !!integration.debugUi,
      debug_api_key_configured: !!readDebugApiKey(),
      script_src: findSdkScriptSrc(),
    };
  }

  function readLatestProcessed() {
    for (let i = 0; i < saves.length; i++) {
      const save = saves[i];
      if (save.processed_status !== 'idle') {
        return {
          status: save.processed_status,
          session_id: save.session_id,
          data: cloneJson(save.processed),
          error: save.processed_error,
        };
      }
    }

    return { status: 'idle', session_id: null, data: null, error: null };
  }

  function fetchProcessedAfterDelay(entry, sessionId) {
    entry.processed_status = 'loading';
    entry.processed_error = null;

    delay(PROCESSED_FETCH_INITIAL_DELAY_MS).then(function () {
      if (saves.indexOf(entry) === -1) return;
      fetchProcessed(entry, sessionId);
    });
  }

  function fetchProcessed(entry, sessionId) {
    const apiKey = readDebugApiKey();
    if (!apiKey) {
      entry.processed_status = 'auth_required';
      entry.processed_error =
        'Processed session requires x-api-key. Set window.__RS_API_KEY__ on the page (same key as the FP demo).';
      return;
    }

    const base = readConfigState().api_host || readDefaultEndpoint();
    const url = String(base).replace(/\/?$/, '') + BB_DEBUG_SESSION_PATH + encodeURIComponent(sessionId);

    entry.processed_status = 'loading';
    entry.processed_error = null;

    getJsonWithRetry(url, 10000, apiKey, PROCESSED_FETCH_RETRIES).then(
      function (response) {
        entry.processed_status = 'ok';
        entry.processed = response;
      },
      function (error) {
        entry.processed_status = 'error';
        entry.processed_error = error && error.message ? error.message : String(error);
      }
    );
  }

  function findSaveEntry(sessionId) {
    if (!sessionId) return saves[0] || null;

    for (let i = 0; i < saves.length; i++) {
      if (saves[i].session_id === sessionId) return saves[i];
    }

    return saves[0] || null;
  }

  function findFirstSavingEntry() {
    for (let i = 0; i < saves.length; i++) {
      if (saves[i].status === 'saving') return saves[i];
    }

    return null;
  }

  function createLateSaveEntry(payload, result) {
    const entry = {
      id: nextSaveId++,
      status: result && result.error ? 'error' : 'ok',
      save_status: result && result.error ? 'error' : 'ok',
      started_at: new Date().toISOString(),
      duration_ms: payload && Number.isFinite(payload.durationMs) ? payload.durationMs : null,
      session_id: result && result.session_id ? result.session_id : null,
      app_id: payload && payload.appId ? payload.appId : null,
      error: result && result.error ? result.error : null,
      hold_timeout: false,
      sent_payload: { metrics: {} },
      processed_status: 'idle',
      processed: null,
      processed_error: null,
    };

    saves.unshift(entry);
    return entry;
  }

  function readDefaultEndpoint() {
    if (rsSDK && rsSDK.defaultEndpoint) return rsSDK.defaultEndpoint;
    return String(config.defaultEndpoint || '').replace(/\/?$/, '');
  }
}

function findSdkScriptSrc() {
  if (typeof document === 'undefined') return '';

  const current = document.currentScript;
  if (isSdkScript(current)) return current.getAttribute('src') || '';

  const scripts = document.getElementsByTagName('script');
  for (let i = scripts.length - 1; i >= 0; i--) {
    if (isSdkScript(scripts[i])) return scripts[i].getAttribute('src') || '';
  }

  return '';
}

function isSdkScript(script) {
  if (!script) return false;

  const src = script.getAttribute('src') || '';
  return src.indexOf('rs-sdk') !== -1;
}

function readDebugApiKey() {
  if (typeof window === 'undefined') return '';

  const value = window[API_KEY_GLOBAL];
  return typeof value === 'string' ? value.trim() : '';
}

function getJsonWithRetry(url, timeoutMs, apiKey, retriesLeft) {
  return getJson(url, timeoutMs, apiKey).catch(function (error) {
    if (!error || error.status !== 404 || retriesLeft <= 0) {
      throw error;
    }

    return delay(PROCESSED_FETCH_RETRY_MS).then(function () {
      return getJsonWithRetry(url, timeoutMs, apiKey, retriesLeft - 1);
    });
  });
}

function delay(ms) {
  return new Promise(function (resolve) {
    setTimeout(resolve, ms);
  });
}

function getJson(url, timeoutMs, apiKey) {
  return new Promise(function (resolve, reject) {
    const xhr = new XMLHttpRequest();
    xhr.responseType = 'json';
    xhr.open('GET', url, true);
    if (apiKey) xhr.setRequestHeader('x-api-key', apiKey);

    let settled = false;
    function fail(message, status) {
      if (settled) return;
      settled = true;
      const error = new Error(message);
      if (status) error.status = status;
      reject(error);
    }

    xhr.onreadystatechange = function () {
      if (xhr.readyState !== XMLHttpRequest.DONE) return;

      if (xhr.status >= 200 && xhr.status < 300) {
        if (settled) return;
        settled = true;
        resolve(xhr.response);
        return;
      }

      fail(processedSessionHttpError(xhr.status), xhr.status);
    };

    xhr.onerror = function () {
      fail('processed session failed: network error');
    };

    xhr.ontimeout = function () {
      fail('processed session failed: timeout');
    };

    xhr.timeout = timeoutMs;
    xhr.send();
  });
}

function processedSessionHttpError(status) {
  if (status === 401 || status === 403) {
    return 'processed session failed: HTTP ' + status + ' (requires x-api-key; BB save already completed)';
  }

  return 'processed session failed: HTTP ' + status;
}

function cloneJson(value) {
  if (value == null) return value;

  try {
    return JSON.parse(JSON.stringify(value));
  } catch (e) {
    return value;
  }
}
